import type { FormInstanceSummary, OrganizationProfile } from "@/modules/docs/api/docAdminApi";
import type { MesBatch } from "@/modules/mes/api/mesApi";
import {
  batchOperationStatusLabel,
  batchStatusLabel,
  ebrStatusLabel,
} from "@/modules/mes/model/batchDetailView";

function esc(value: string | number | null | undefined): string {
  return String(value ?? "—")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Trạng thái biểu mẫu DOC trên bản in EBR. */
function formStatusLabel(status: string): string {
  switch (status) {
    case "Completed":
    case "Closed":
      return "Đã hoàn tất";
    case "Submitted":
      return "Đã gửi";
    case "InProgress":
    case "Open":
      return "Đang ghi";
    case "Draft":
      return "Nháp";
    case "Cancelled":
      return "Đã hủy";
    default:
      return status;
  }
}

/** Printable HTML for EBR dossier preview (in-app modal + browser print). */
export function buildEbrPrintHtml(
  batch: MesBatch,
  forms: FormInstanceSummary[],
  org?: OrganizationProfile | null,
): string {
  const formRows =
    forms.length === 0
      ? `<tr><td colspan="4">Chưa có biểu mẫu gắn mẻ.</td></tr>`
      : forms
          .map(
            (row) => `<tr>
              <td class="mono">${esc(row.docCode)}</td>
              <td>${esc(row.titleVi)}</td>
              <td>${esc(row.workDate)}</td>
              <td>${esc(formStatusLabel(row.status))}</td>
            </tr>`,
          )
          .join("");

  const opRows =
    batch.operations.length === 0
      ? `<tr><td colspan="4">Chưa có công đoạn.</td></tr>`
      : batch.operations
          .map(
            (op) => `<tr>
        <td>${esc(op.seq)}</td>
        <td class="mono">${esc(op.operationCode)}</td>
        <td>${esc(op.opName)}</td>
        <td>${esc(batchOperationStatusLabel(op.status))}</td>
      </tr>`,
          )
          .join("");

  const logo = org?.printLogoReadUri
    ? `<img src="${esc(org.printLogoReadUri)}" alt="" class="logo" />`
    : "";
  const brand = org?.brandName
    ? `<p class="brand">${esc(org.brandName)}</p>`
    : "";
  const legal = org?.legalName
    ? `<p class="meta">${esc(org.legalName)}${
        org.taxId ? ` · MST ${esc(org.taxId)}` : ""
      }</p>`
    : "";
  const address = org?.address
    ? `<p class="meta">${esc(org.address)}</p>`
    : "";
  const footer = org?.printFooterText
    ? `<footer class="footer">${esc(org.printFooterText)}</footer>`
    : "";

  return `<!DOCTYPE html>
<html lang="vi">
<head>
  <meta charset="utf-8"/>
  <title>Hồ sơ mẻ · ${esc(batch.batchCode)}</title>
  <style>
    body { font-family: system-ui, -apple-system, Segoe UI, sans-serif; font-size: 12px; color: #111; margin: 24px; }
    .logo { max-height: 48px; object-fit: contain; display: block; margin-bottom: 8px; }
    .brand { margin: 0; font-size: 12px; color: #555; font-weight: 600; }
    .meta { margin: 2px 0 0; font-size: 12px; color: #555; }
    .screen-id { margin: 0; font-size: 11px; letter-spacing: 0.04em; color: #555; }
    .doc-title {
      text-align: center;
      font-size: 18px;
      font-weight: 700;
      margin: 28px 0 20px;
      line-height: 1.3;
    }
    .section-title { font-size: 13px; font-weight: 600; margin: 20px 0 8px; }
    table.data {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
      margin: 0 0 4px;
    }
    table.data th,
    table.data td {
      border: 0.5px solid #ccc;
      padding: 6px 8px;
      vertical-align: top;
      text-align: left;
    }
    table.data th { background: #f5f5f5; font-weight: 600; }
    .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
    .footer { margin-top: 24px; padding-top: 8px; border-top: 0.5px solid #ddd; font-size: 10px; color: #666; }
  </style>
</head>
<body>
  <header>
    ${logo}
    ${brand}
    <p class="screen-id">SCR-PRT-EBR</p>
    ${legal}
    ${address}
  </header>

  <h1 class="doc-title">Hồ sơ mẻ · ${esc(batch.batchCode)}</h1>

  <p class="section-title">Hồ sơ mẻ điện tử (bản in tóm tắt)</p>
  <table class="data">
    <tbody>
      <tr><th style="width:10rem">Mã mẻ</th><td class="mono">${esc(batch.batchCode)}</td></tr>
      <tr><th>Lệnh sản xuất</th><td class="mono">${esc(batch.orderNo)}</td></tr>
      <tr><th>Nhà máy</th><td>${esc(batch.plantCode)}</td></tr>
      <tr><th>Trạng thái mẻ</th><td>${esc(batchStatusLabel(batch.status))}</td></tr>
      <tr><th>Hồ sơ mẻ</th><td>${esc(ebrStatusLabel(batch.ebrStatus))}</td></tr>
      <tr><th>Op hoàn thành</th><td>${esc(batch.completedOps)}/${esc(batch.operationCount)}</td></tr>
    </tbody>
  </table>

  <p class="section-title">Biểu mẫu công đoạn</p>
  <table class="data">
    <thead>
      <tr><th>DOC</th><th>Tiêu đề</th><th>Ngày</th><th>TT</th></tr>
    </thead>
    <tbody>${formRows}</tbody>
  </table>

  <p class="section-title">Công đoạn</p>
  <table class="data">
    <thead>
      <tr><th>Seq</th><th>Mã</th><th>Tên</th><th>TT</th></tr>
    </thead>
    <tbody>${opRows}</tbody>
  </table>
  ${footer}
</body>
</html>`;
}
